import {
  Children,
  cloneElement,
  createElement,
  isValidElement,
  type CSSProperties,
  type JSX,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { Components, ExtraProps } from 'react-markdown';

const HexColorRegex = /#(?:[0-9a-fA-F]{3}){1,2}\b/gi;
const DotWordBreakRegex = /(\w)\.(\w)/g;

function insertDotBreaks(text: string): string {
  return text.replace(DotWordBreakRegex, '$1.\u200b$2');
}

function ColorChip({ hex }: { hex: string }) {
  const style = {
    '--chip-color': hex,
  } as CSSProperties;

  return (
    <span className="color-chip" style={style} title={hex}>
      {hex}
    </span>
  );
}

function splitTextWithColors(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let colorIndex = 0;

  for (const match of text.matchAll(HexColorRegex)) {
    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      nodes.push(insertDotBreaks(text.slice(lastIndex, matchIndex)));
    }

    const hexValue = match[0].toUpperCase().slice(0, 7);
    nodes.push(
      <ColorChip key={`${keyPrefix}-color-${colorIndex}`} hex={hexValue} />,
    );
    colorIndex += 1;
    lastIndex = matchIndex + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(insertDotBreaks(text.slice(lastIndex)));
  }

  return nodes;
}

function renderWithColorSwatches(
  children: ReactNode,
  keyPrefix: string,
): ReactNode {
  return Children.map(children, (child, index) => {
    if (typeof child === 'string') {
      return splitTextWithColors(child, `${keyPrefix}-${index}`);
    }

    if (isValidElement(child)) {
      const element = child as ReactElement<any, any>;
      if (element.props && element.props.children !== undefined) {
        return cloneElement(
          element,
          undefined,
          renderWithColorSwatches(
            element.props.children,
            `${keyPrefix}-${index}`,
          ),
        );
      }
    }

    return child;
  });
}

const colorSwatchTags = [
  'p',
  'li',
  'blockquote',
  'td',
  'th',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
] as const;

type ColorSwatchTag = (typeof colorSwatchTags)[number];
type ColorSwatchComponentProps = JSX.IntrinsicElements[ColorSwatchTag] &
  ExtraProps;

export const MarkdownColorComponents: Components = colorSwatchTags.reduce(
  (components, tag) => {
    (components as any)[tag] = function MarkdownColorComponent({
      node: _node,
      children,
      ...props
    }: ColorSwatchComponentProps) {
      // Filter out ExtraProps that might be incompatible with the specific tag
      const { node, ...filteredProps } = props as any;
      return createElement(
        tag,
        filteredProps,
        renderWithColorSwatches(children, tag),
      );
    };

    return components;
  },
  {} as Components,
);
